import React, { useState, useEffect } from "react";
import { TextField, Button, MenuItem, Select, Autocomplete, Grid, InputLabel, FormControl, FormControlLabel, Checkbox, Typography, Divider } from "@mui/material";
import { driverService } from '../services/api';
import { getDieselRate } from '../utils/settings';

const gujaratCities = [
  "Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar", "Gandhinagar",
  "Junagadh", "Anand", "Nadiad", "Navsari", "Morbi", "Bharuch", "Mehsana", "Tulsigam"
  // ...add more as needed
];

const initialForm = {
  truckNumber: "",
  startDate: "",
  endDate: "",
  from: "",
  to: "",
  partyName: "",
  compressor: "No",
  startKm: "",
  endKm: "",
  dieselQty: "",
  dieselAmount: "",
  toll: "",
  driverSalary: "",
  advancedSalary: "",
  driverName: "",
  maintenance: "",
  freight: "",
  weight: "",
  // Return trip fields
  isRoundTrip: false,
  returnFrom: "",
  returnTo: "",
  returnFreight: "",
  returnWeight: "",
  // totalFreight and totalKm will be calculated
};

export default function TripEntryForm({ trucks, addTrip }) {
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [dieselRate, setDieselRate] = useState(getDieselRate);

  // Fetch drivers on component mount
  useEffect(() => {
    async function fetchDrivers() {
      try {
        const response = await driverService.getAll();
        setDrivers(response.data?.data || response.data || []);
      } catch (err) {
        console.error('Error fetching drivers:', err);
      }
    }
    fetchDrivers();
  }, []);

  // Keep diesel rate in sync with Settings page changes
  useEffect(() => {
    const syncRate = () => setDieselRate(getDieselRate());
    window.addEventListener('settingsUpdated', syncRate);
    window.addEventListener('storage', syncRate);
    return () => {
      window.removeEventListener('settingsUpdated', syncRate);
      window.removeEventListener('storage', syncRate);
    };
  }, []);

  const requiredFields = [
    "truckNumber", "startDate", "endDate", "from", "to", "partyName", "startKm", "endKm", "dieselQty", "dieselAmount", "toll", "driverSalary", "driverName", "maintenance", "freight", "weight"
  ];

  const returnTripRequiredFields = [
    "returnFrom", "returnTo", "returnFreight", "returnWeight"
  ];

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Auto-calculate Total Freight
  const freight = parseFloat(form.freight) || 0;
  const weight = parseFloat(form.weight) || 0;
  const totalFreight = freight && weight ? parseFloat((freight * weight).toFixed(2)) : "";

  // Auto-calculate Total KM
  const startKm = parseInt(form.startKm) || 0;
  const endKm = parseInt(form.endKm) || 0;
  const totalKm = form.startKm !== "" && form.endKm !== "" ? endKm - startKm : "";

  // Calculations
  const totalExpenses =
    Number(form.dieselAmount || 0) +
    Number(form.toll || 0) +
    Number(form.driverSalary || 0) +
    Number(form.maintenance || 0);
  const totalProfit = totalFreight !== "" ? totalFreight - totalExpenses : "";
  const driverSalary = Number(form.driverSalary || 0);
  const advancedSalary = Number(form.advancedSalary || 0);
  const totalBalance =
    form.driverSalary !== "" || form.advancedSalary !== ""
      ? parseFloat((driverSalary - advancedSalary).toFixed(2))
      : "";
  const tripDays =
    form.startDate && form.endDate
      ? (new Date(form.endDate) - new Date(form.startDate)) / (1000 * 60 * 60 * 24) + 1
      : 1;
  const perDayProfit = tripDays > 0 && totalProfit !== "" ? totalProfit / tripDays : 0;

  const handleRoundTripToggle = (e) => {
    const checked = e.target.checked;
    setIsRoundTrip(checked);
    if (!checked) {
      // Clear return trip fields when unchecked
      setForm({
        ...form,
        isRoundTrip: false,
        returnFrom: "",
        returnTo: "",
        returnFreight: "",
        returnWeight: ""
      });
    } else {
      setForm({ ...form, isRoundTrip: true });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Always update the state so the user can type
    const newForm = { ...form, [name]: value };

    // Auto-calculate Diesel Amount when Diesel Qty changes
    if (name === 'dieselQty') {
      const dieselQty = Number(value) || 0;
      newForm.dieselAmount = dieselQty * dieselRate;
    }

    // Validate End KM is greater than Start KM, but do not block typing
    let error = "";
    if ((name === 'startKm' || name === 'endKm')) {
      const startKm = Number(name === 'startKm' ? value : newForm.startKm) || 0;
      const endKm = Number(name === 'endKm' ? value : newForm.endKm) || 0;
      if (newForm.startKm !== "" && newForm.endKm !== "" && endKm <= startKm) {
        error = "End KM must be greater than Start KM";
      }
      // Calculate Total KM if both are valid numbers
      if (!error && newForm.startKm !== "" && newForm.endKm !== "") {
        newForm.totalKm = endKm - startKm;
      } else {
        newForm.totalKm = "";
      }
    }

    // Calculate Total Freight
    if (name === 'freight' || name === 'weight') {
      const freight = Number(newForm.freight) || 0;
      const weight = Number(newForm.weight) || 0;
      newForm.totalFreight = freight * weight;
    }

    // Calculate Total Expenses (also trigger when dieselQty changes since it affects dieselAmount)
    if (name === 'dieselQty' || name === 'dieselAmount' || name === 'toll' || name === 'driverSalary' || 
        name === 'maintenance') {
      const dieselAmount = Number(newForm.dieselAmount) || 0;
      const toll = Number(newForm.toll) || 0;
      const driverSalaryAmt = Number(newForm.driverSalary) || 0;
      const maintenance = Number(newForm.maintenance) || 0;
      newForm.totalExpenses = dieselAmount + toll + driverSalaryAmt + maintenance;
    }

    // Calculate Total Balance (Driver Salary - Advanced Salary)
    if (name === 'driverSalary' || name === 'advancedSalary') {
      const driverSalaryAmt = Number(newForm.driverSalary) || 0;
      const advancedSalaryAmt = Number(newForm.advancedSalary) || 0;
      newForm.totalBalance = parseFloat((driverSalaryAmt - advancedSalaryAmt).toFixed(2));
    }

    // Calculate Total Profit and Per Day Profit only if relevant fields changed
    if (name === 'dieselQty' || name === 'dieselAmount' || name === 'toll' || name === 'driverSalary' || 
        name === 'maintenance' || name === 'freight' || name === 'weight' || 
        name === 'startDate' || name === 'endDate') {
      const totalFreightAmt = Number(newForm.totalFreight) || 0;
      const totalExpensesAmt = Number(newForm.totalExpenses) || 0;
      newForm.totalProfit = totalFreightAmt - totalExpensesAmt;

      // Calculate Per Day Profit
      const startDate = new Date(newForm.startDate);
      const endDate = new Date(newForm.endDate);
      const days = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
      newForm.perDayProfit = newForm.totalProfit / days;
    }

    // Handle return trip calculations
    if (name.startsWith('return')) {
      // Return trip freight calculation
      if (name === 'returnFreight' || name === 'returnWeight') {
        const returnFreight = Number(newForm.returnFreight) || 0;
        const returnWeight = Number(newForm.returnWeight) || 0;
        newForm.returnTotalFreight = returnFreight * returnWeight;
      }

      // Return trip profit calculation (no expenses for return, just freight)
      if (name === 'returnFreight' || name === 'returnWeight') {
        const returnTotalFreight = Number(newForm.returnTotalFreight) || 0;
        // No expenses for return trip, profit = freight
        newForm.returnTotalProfit = returnTotalFreight;
      }
    }

    setForm(newForm);
    setFormError(error); // Only set error, do not block typing
  };

  // Calculate return trip values
  const returnTotalFreight = (parseFloat(form.returnFreight) || 0) * (parseFloat(form.returnWeight) || 0);
  // No expenses for return trip, profit = freight
  const returnTotalProfit = returnTotalFreight;

  const handleSubmit = (e) => {
    e.preventDefault();
    // Client-side validation for required fields
    const missing = requiredFields.filter(f => !form[f] && form[f] !== 0);
    if (missing.length > 0) {
      setFormError("Please fill all required fields: " + missing.join(", "));
      return;
    }

    // Validate return trip fields if round trip is selected
    if (isRoundTrip) {
      const returnMissing = returnTripRequiredFields.filter(f => !form[f] && form[f] !== 0);
      if (returnMissing.length > 0) {
        setFormError("Please fill all return trip fields: " + returnMissing.join(", "));
        return;
      }
    }

    setFormError("");
    
    const tripData = {
      ...form,
      id: Date.now(),
      advancedSalary: Number(form.advancedSalary || 0),
      totalFreight,
      totalKm,
      totalExpenses,
      totalProfit,
      perDayProfit,
      totalBalance,
      isRoundTrip: isRoundTrip,
    };

    // Add return trip data if round trip
    if (isRoundTrip) {
      tripData.returnTotalFreight = returnTotalFreight;
      tripData.returnTotalProfit = returnTotalProfit;
    }

    addTrip(tripData);
    setForm(initialForm);
    setIsRoundTrip(false);
    setSuccessMsg(isRoundTrip ? "Round trip added successfully!" : "Trip added successfully!");
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="h5" gutterBottom>Trip Entry</Typography>
      {formError && <div style={{ color: 'red', marginBottom: 8 }}>{formError}</div>}
      {successMsg && <div style={{ color: 'green', marginBottom: 8 }}>{successMsg}</div>}
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isRoundTrip}
                onChange={handleRoundTripToggle}
                name="isRoundTrip"
              />
            }
            label="Round Trip (Include Return Journey)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            freeSolo
            options={trucks}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.truck_number || '';
            }}
            value={trucks.find(t => t.truck_number === form.truckNumber) || null}
            onChange={(event, newValue) => {
              if (newValue && typeof newValue === 'object') {
                setForm({ ...form, truckNumber: newValue.truck_number });
              } else if (typeof newValue === 'string') {
                setForm({ ...form, truckNumber: newValue });
              } else {
                setForm({ ...form, truckNumber: '' });
              }
            }}
            onInputChange={(event, newValue) => {
              // Allow typing truck number directly
              if (event && event.type === 'change') {
                setForm({ ...form, truckNumber: newValue });
              }
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Truck Number" 
                name="truckNumber"
                fullWidth
                required
              />
            )}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                option.truck_number.toLowerCase().includes(searchTerm)
              );
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth required>
            <InputLabel>Driver Name</InputLabel>
            <Select
              name="driverName"
              value={form.driverName}
              onChange={handleChange}
              label="Driver Name"
            >
              <MenuItem value="">Select Driver</MenuItem>
              {[...new Map(drivers.map(driver => [driver.name, driver])).values()].map((driver) => (
                <MenuItem key={driver.id} value={driver.name}>
                  {driver.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Start Date" name="startDate" type="date" value={form.startDate} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="End Date" name="endDate" type="date" value={form.endDate} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            freeSolo
            options={gujaratCities}
            value={form.from}
            onInputChange={(event, newValue) => setForm({ ...form, from: newValue })}
            renderInput={(params) => <TextField {...params} label="From" name="from" fullWidth />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            freeSolo
            options={gujaratCities}
            value={form.to}
            onInputChange={(event, newValue) => setForm({ ...form, to: newValue })}
            renderInput={(params) => <TextField {...params} label="To" name="to" fullWidth />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Party Name" name="partyName" value={form.partyName} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel id="compressor-label">Compressor</InputLabel>
            <Select
              labelId="compressor-label"
              label="Compressor"
              name="compressor"
              value={form.compressor}
              onChange={handleChange}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField 
            label="Start KM" 
            name="startKm" 
            type="number" 
            value={form.startKm === undefined || form.startKm === null ? '' : form.startKm} 
            onChange={handleInputChange}
            fullWidth 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField 
            label="End KM" 
            name="endKm" 
            type="number" 
            value={form.endKm === undefined || form.endKm === null ? '' : form.endKm} 
            onChange={handleInputChange}
            error={!!formError && formError.includes('End KM')}
            helperText={!!formError && formError.includes('End KM') ? formError : ''}
            fullWidth 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Total KM" name="totalKm" type="number" value={totalKm} InputProps={{ readOnly: true }} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField 
            label="Diesel Qty (Liters)" 
            name="dieselQty" 
            type="number" 
            value={form.dieselQty} 
            onChange={handleInputChange} 
            fullWidth 
            helperText={`Price: ₹${dieselRate} per liter`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField 
            label="Diesel Amount" 
            name="dieselAmount" 
            type="number" 
            value={form.dieselAmount} 
            onChange={handleInputChange} 
            fullWidth 
            helperText={`Auto-calculated: Diesel Qty × ₹${dieselRate} (editable if needed)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Toll" name="toll" type="number" value={form.toll} onChange={handleInputChange} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Driver Salary" name="driverSalary" type="number" value={form.driverSalary} onChange={handleInputChange} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Advanced Salary" name="advancedSalary" type="number" value={form.advancedSalary} onChange={handleInputChange} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Total Balance"
            name="totalBalance"
            type="number"
            value={totalBalance}
            InputProps={{ readOnly: true }}
            helperText="Driver Salary − Advanced Salary"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Maintenance" name="maintenance" type="number" value={form.maintenance} onChange={handleInputChange} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Freight" name="freight" type="number" value={form.freight} onChange={handleInputChange} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Weight" name="weight" type="number" value={form.weight} onChange={handleInputChange} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Total Freight" name="totalFreight" type="number" value={totalFreight} InputProps={{ readOnly: true }} fullWidth />
        </Grid>
      </Grid>
      
      {/* Show forward trip summary only if NOT a round trip */}
      {!isRoundTrip && (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Typography variant="subtitle1"><b>Forward Trip Summary:</b></Typography>
          <b>Total KM:</b> {totalKm} &nbsp;
          <b>Total Expenses:</b> {totalExpenses} &nbsp;
          <b>Total Profit:</b> {totalProfit} &nbsp;
          <b>Total Balance:</b> {totalBalance} &nbsp;
          <b>Per Day Profit:</b> {perDayProfit !== "" ? perDayProfit.toFixed(2) : ""}
        </div>
      )}

      {/* Return Trip Section */}
      {isRoundTrip && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Return Trip Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                options={gujaratCities}
                value={form.returnFrom}
                onInputChange={(event, newValue) => setForm({ ...form, returnFrom: newValue })}
                renderInput={(params) => <TextField {...params} label="Return From" name="returnFrom" fullWidth required={isRoundTrip} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                options={gujaratCities}
                value={form.returnTo}
                onInputChange={(event, newValue) => setForm({ ...form, returnTo: newValue })}
                renderInput={(params) => <TextField {...params} label="Return To" name="returnTo" fullWidth required={isRoundTrip} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField 
                label="Return Freight" 
                name="returnFreight" 
                type="number" 
                value={form.returnFreight} 
                onChange={handleInputChange} 
                fullWidth 
                required={isRoundTrip}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField 
                label="Return Weight" 
                name="returnWeight" 
                type="number" 
                value={form.returnWeight} 
                onChange={handleInputChange} 
                fullWidth 
                required={isRoundTrip}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField 
                label="Return Total Freight" 
                name="returnTotalFreight" 
                type="number" 
                value={returnTotalFreight} 
                InputProps={{ readOnly: true }} 
                fullWidth 
              />
            </Grid>
          </Grid>
          
          {/* Show only combined summary for round trips */}
          <div style={{ marginTop: 16, marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
            <Typography variant="subtitle1"><b>Combined Round Trip Summary:</b></Typography>
            <b>Total KM:</b> {totalKm} &nbsp;
            <b>Total Expenses:</b> {totalExpenses} &nbsp;
            <b>Total Profit:</b> {(parseFloat(totalProfit) || 0) + (parseFloat(returnTotalProfit) || 0)} &nbsp;
            <b>Total Freight:</b> {(parseFloat(totalFreight) || 0) + (parseFloat(returnTotalFreight) || 0)} &nbsp;
            <b>Total Balance:</b> {totalBalance}
          </div>
        </>
      )}

      <Button type="submit" variant="contained" style={{ marginTop: 16 }} fullWidth>
        {isRoundTrip ? "Save Round Trip" : "Save Trip"}
      </Button>
    </form>
  );
} 